import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from 'moment-timezone';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import { getRole, getUserName } from '../utils/common';
import * as axiosInstance from '../utils/axiosinstace'
import { Role } from '../utils/role';
import { useTranslation } from 'react-i18next';

const Users = () => {
  const { t } = useTranslation(['user', 'common']);
    const [data, setData] = useState([])
    const [role] = useState(getRole());
    const [signedInAs] = useState(getUserName());
    // Pending toggle, held while the confirm dialog is open. null = closed.
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    // Search + filters. All client-side: /fetchAllUsers returns the caller's
    // whole list in one go (120 rows today, scoped by createdBy), so filtering
    // here avoids a round trip per keystroke and keeps the API untouched.
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [farmerFilter, setFarmerFilter] = useState('');
    const [pendingOnly, setPendingOnly] = useState(false);

    const isAdmin = role == Role.Admin;

    // A user row's own status column. This used to read row.runningBatch --
    // a farmmaster field that does not exist on a user -- so every account
    // rendered green regardless, and a disabled login looked active.
    const statusOf = (row) => String(row.status || '').toUpperCase();

    // The list carries roleID but no role name -- the roleName(roleID) SQL
    // function is only used by the profile endpoint. Mapping locally also keeps
    // the label translatable, which a server-supplied name would not be.
    const ROLE_LABELS = {
      [Role.Admin]: 'common:roles.admin',
      [Role.Farmer]: 'common:roles.farmer',
      [Role.Worker]: 'common:roles.worker',
      [Role.Digiviewer]: 'common:roles.viewer',
    };
    // Chip modifier per role -- see .roleChip in App.css. Keyed off roleID, not
    // the translated label, so the colour cannot drift when the language changes.
    const ROLE_CHIPS = {
      [Role.Admin]: 'admin',
      [Role.Farmer]: 'farmer',
      [Role.Worker]: 'worker',
      [Role.Digiviewer]: 'viewer',
    };
    const roleLabel = (roleID) => t(ROLE_LABELS[String(roleID)] || 'common:roles.unknown');
    const roleChip = (roleID) => `roleChip ${ROLE_CHIPS[String(roleID)] || 'unknown'}`;

    // farmowner holds the OWNING farmer's userName, and is populated only for
    // Workers and Viewers -- Farmers and the Admin have it null. Resolve it to a
    // display name off the same list rather than asking the API for one: every
    // farmer is already a row here.
    const nameByUserName = {};
    data.forEach((r) => { if (r.userName) nameByUserName[r.userName] = r.farmerName || r.userName; });
    const farmerOptions = Array.from(
      new Set(data.map((r) => r.farmowner).filter((f) => f && String(f).trim()))
    ).sort((a, b) => (nameByUserName[a] || a).localeCompare(nameByUserName[b] || b));

    const matches = (row) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const haystack = [row.farmerName, row.userName, row.phoneNumber, row.emailID]
          .map((v) => String(v == null ? '' : v).toLowerCase());
        if (!haystack.some((v) => v.indexOf(q) !== -1)) return false;
      }
      if (roleFilter && String(row.roleID) !== roleFilter) return false;
      if (statusFilter && statusOf(row) !== statusFilter) return false;
      // "Farmer wise" means that farmer AND the staff under them, which is why
      // this matches userName as well as farmowner.
      if (farmerFilter && row.farmowner !== farmerFilter && row.userName !== farmerFilter) return false;
      if (pendingOnly && row.passwordSet !== false) return false;
      return true;
    };

    const filtered = data.filter(matches);
    const filtersActive = !!(search || roleFilter || statusFilter || farmerFilter || pendingOnly);

    const clearFilters = () => {
      setSearch(''); setRoleFilter(''); setStatusFilter(''); setFarmerFilter(''); setPendingOnly(false);
    };

    // react-select for the farmer picker only -- a native <select> has no
    // typeahead and there are 23 owners today, growing with every farmer added.
    // react-select was already a declared dependency (unused until now), so this
    // costs no new install, and it avoids the jQuery init/destroy lifecycle that
    // select2 needs in React plus select2's vertical-centering quirk.
    const farmerSelectOptions = farmerOptions.map((f) => ({
      value: f,
      label: nameByUserName[f] || f,
    }));
    // Sized to sit level with the .form-control-sm controls beside it.
    const farmerSelectStyles = {
      control: (base) => ({ ...base, minHeight: 31, height: 31, fontSize: 12, borderColor: '#ddd' }),
      valueContainer: (base) => ({ ...base, padding: '0 6px' }),
      input: (base) => ({ ...base, margin: 0, padding: 0 }),
      indicatorsContainer: (base) => ({ ...base, height: 31 }),
      dropdownIndicator: (base) => ({ ...base, padding: 4 }),
      clearIndicator: (base) => ({ ...base, padding: 4 }),
      option: (base) => ({ ...base, fontSize: 12, padding: '6px 10px' }),
      // Portalled so the menu is never clipped by the card it sits in.
      menuPortal: (base) => ({ ...base, zIndex: 1060 }),
    };

    const columns = [
        {
          name: t('common:fields.name'),
          selector: "farmerName",
          sortable: true,
          // Name and Email are the only two flexible columns; everything else is
          // pinned. In react-data-table-component a `width` emits BOTH min-width
          // and max-width, so a fixed column cannot absorb slack -- which is why
          // Email, previously the only one on `minWidth`, swallowed every spare
          // pixel and looked enormous. grow splits the remainder 3:2 in Name's
          // favour, and both keep a floor so neither collapses when narrow.
          minWidth: '180px',
          grow: 3
        },
        {
          name: t('common:fields.userName'),
          selector: "userName",
          sortable: true,
          width: '120px'
        },
        {
          name: t('common:fields.role'),
          selector: "roleID",
          sortable: true,
          width: '110px',
          cell: row => <span className={roleChip(row.roleID)}>{roleLabel(row.roleID)}</span>
        },
        {
            name: t('common:fields.phoneNumber'),
            selector: "phoneNumber",
            sortable: true,
            width: '130px'
          },
        {
          name: t('common:fields.emailId'),
          selector: "emailID",
          sortable: true,
          minWidth: '160px',
          grow: 2
        },
        {
            name: t('common:fields.createdDate'),
            selector: "createdDate",
            sortable: true,
            width: '150px',
            // 'DD-MMM-YYYY' is the app's dominant date format (18 call sites),
            // and moment's locale follows i18next, so this reads "26-Ogs-2026"
            // in Malay -- see i18n/locale.js.
            //
            // Pinned to Asia/Singapore per CLAUDE.md rather than the viewer's
            // own zone: the API sends a UTC ISO string, so a plain moment() has
            // an admin abroad seeing a different created date for the same row
            // than a colleague in Malaysia. Integrators.js does use a plain
            // moment() here -- same output for anyone actually in SGT.
            cell: row => (
              <div>{row.createdDate
                ? moment(row.createdDate).tz('Asia/Singapore').format('DD-MMM-YYYY')
                : ''}</div>
            )
        },
        {
            name: t('common:fields.status'),
            selector: "status",
            sortable: true,
            width: '110px',
            // Own chip classes rather than the global .greenbg/.redbg: those are
            // shared with ListofBarns/Integrators/Templates/MyFarms, so
            // restyling them to match the role chips would have altered four
            // other screens.
            cell: row => (
              <div>
                {statusOf(row) === 'INACTIVE'
                  ? <span className="statusChip inactive">{t('common:state.inactive')}</span>
                  : <span className="statusChip active">{t('common:state.active')}</span>}
              </div>
            )
        }
      ];

    // Who can still be sent a set-password invite: an account that has not
    // accepted one yet (passwordSet false, computed by routes/listofusers.js)
    // and has somewhere to send it. Workers never get invites -- their password
    // is typed by whoever creates them -- so only Farmers and Viewers qualify.
    const canBeInvited = (row) =>
      row.passwordSet === false &&
      (String(row.roleID) === Role.Farmer || String(row.roleID) === Role.Digiviewer);

    // The mirror image: an account that HAS a password and has lost it. Workers
    // are included here although they are excluded above -- a worker's first
    // password is typed for them by whoever created the account, but once it is
    // set nobody holds a copy, which is the whole reason this action exists.
    // Admins are excluded: a reset link overwrites a live credential, so one
    // admin resetting another would be a lateral takeover.
    //
    // === true, for the same reason canBeInvited uses === false: a row that
    // arrives without the field (an older API, a stale cache) must offer nothing
    // rather than guess, and guessing wrong here mails a password link.
    const canBeReset = (row) =>
      row.passwordSet === true &&
      (String(row.roleID) === Role.Farmer ||
        String(row.roleID) === Role.Worker ||
        String(row.roleID) === Role.Digiviewer);

    // At most ONE mail action per row. The two gates partition on passwordSet so
    // they can never both apply -- which is what lets the "no email address" hint
    // below be written once instead of once per action, and why the Actions
    // column still holds at most two icons and stays at 110px.
    //
    // A disabled account is excluded from both: the link would land in the inbox
    // of someone who still cannot sign in, and the API refuses it anyway.
    const mailAction = (row) => {
      if (statusOf(row) === 'INACTIVE') return null;
      if (canBeInvited(row)) return 'invite';
      if (canBeReset(row)) return 'reset';
      return null;
    };

    // Icon, colour and label per mail action -- see .userStatusBtn.invite /
    // .reset in App.css. A map rather than inline ternaries so a third action
    // would be an entry, not another branch.
    const MAIL_UI = {
      invite: { cls: 'userStatusBtn invite', icon: 'fas fa-envelope', label: t('user:list.resendInvite') },
      reset: { cls: 'userStatusBtn reset', icon: 'fas fa-key', label: t('user:list.resetPassword') },
    };

    // The toggle is Admin-only, and the API refuses to change your own status
    // (locking yourself out is unrecoverable from the UI), so the button is
    // omitted on your own row rather than offered and then rejected.
    if (isAdmin) {
      columns.push({
        name: t('user:list.actions'),
        width: '110px',
        // Icons, not bootstrap buttons: row actions elsewhere in the app are
        // bare FontAwesome icons (.view-btn, see Integrators.js), and a
        // btn-outline-* in a dense table row dwarfs the data beside it. Kept
        // as real <button>s rather than bare <i>s so they stay keyboard
        // reachable -- the icon alone carries no semantics.
        cell: row => {
          if (row.userName === signedInAs) {
            return <span className="notEntered">{t('user:list.you')}</span>;
          }
          const disabling = statusOf(row) !== 'INACTIVE';
          const label = disabling
            ? t('common:actions.deactivate')
            : t('common:actions.activate');
          // At most one of invite/reset, so hasEmail is branched ONCE here rather
          // than inside each action.
          const mail = mailAction(row);
          const ui = mail ? MAIL_UI[mail] : null;
          const hasEmail = !!(row.emailID && String(row.emailID).trim());
          return (
            <>
              <button
                type="button"
                className={disabling ? 'userStatusBtn' : 'userStatusBtn enable'}
                title={label}
                aria-label={label}
                onClick={() => { setResult(null); setPending({ row, kind: 'status', disabling }); }}
              >
                <i className={disabling ? 'fas fa-user-slash' : 'fas fa-user-check'} aria-hidden="true" />
              </button>
              {ui && hasEmail ? (
                <button
                  type="button"
                  className={ui.cls}
                  title={ui.label}
                  aria-label={ui.label}
                  onClick={() => { setResult(null); setPending({ row, kind: mail }); }}
                >
                  <i className={ui.icon} aria-hidden="true" />
                </button>
              ) : null}
              {ui && !hasEmail ? (
                // Nothing to send to, for either kind of link. A greyed,
                // non-interactive version of the same icon rather than a button
                // the API would only refuse.
                //
                // NOT the .notEntered pill used for the "You" cell: that is
                // UPPERCASE with letter-spacing, and "No email address" in a
                // 110px column wrapped to one character per line and tripled the
                // row height. The tooltip carries the explanation instead, and
                // aria-label carries it for screen readers.
                <span
                  className="userStatusBtn muted"
                  title={t('user:list.noEmail')}
                  aria-label={t('user:list.noEmail')}
                  role="img"
                >
                  <i className={ui.icon} aria-hidden="true" />
                </span>
              ) : null}
            </>
          );
        }
      });
    }

    const getAllUsers  = async ()=>{
      try {
        const response = await axiosInstance.getAllUsers();
        setData(response.list)
      } catch (err) {
        console.log(err);
      }
    }

    const displayName = (row) => row.farmerName || row.userName;

    // One descriptor per row action. Everything that differs between them --
    // which call to make, what the dialog says, how the confirm button looks,
    // which message to fall back to when the request throws -- lives in a single
    // entry. It used to live in six functions that all had to be edited in step,
    // and `const isInvite = kind === 'invite'` only worked while there were
    // exactly two kinds.
    const ACTIONS = {
      status: {
        run: (p) => axiosInstance.updateUserStatus(p.row.id, p.disabling ? 'INACTIVE' : 'ACTIVE'),
        title: (p) => t(p.disabling ? 'user:list.confirmDisableTitle' : 'user:list.confirmEnableTitle'),
        body: (p) => t(p.disabling ? 'user:list.confirmDisableBody' : 'user:list.confirmEnableBody',
                       { name: displayName(p.row) }),
        confirmLabel: (p) => t(p.disabling ? 'common:actions.deactivate' : 'common:actions.activate'),
        confirmClass: (p) => (p.disabling ? 'btn btn-danger' : 'btn btn-success'),
        failed: () => t('user:list.updateFailed'),
      },
      invite: {
        run: (p) => axiosInstance.resendInvite(p.row.id),
        title: () => t('user:list.confirmResendTitle'),
        body: (p) => t('user:list.confirmResendBody', { name: displayName(p.row), email: p.row.emailID }),
        confirmLabel: () => t('user:list.sendInvite'),
        // Neither danger nor success for the two mail actions: sending a link is
        // not destructive, and it is not the "re-enable" affirmative either.
        confirmClass: () => 'btn btn-primary',
        failed: () => t('user:list.inviteFailed'),
      },
      reset: {
        run: (p) => axiosInstance.sendPasswordReset(p.row.id),
        title: () => t('user:list.confirmResetTitle'),
        body: (p) => t('user:list.confirmResetBody', { name: displayName(p.row), email: p.row.emailID }),
        confirmLabel: () => t('user:list.sendReset'),
        confirmClass: () => 'btn btn-primary',
        failed: () => t('user:list.resetFailed'),
      },
    };

    // null while the dialog is closed, and also for a `kind` with no entry -- so
    // a typo renders no dialog rather than one that throws on open.
    const action = pending ? ACTIONS[pending.kind] : null;

    const confirmPending = async () => {
      if (!pending || !action) return;
      setBusy(true);
      try {
        const response = await action.run(pending);
        setResult({ error: !response.status, message: response.message });
        // Refresh on success: a status change moves the badge, and while neither
        // mail action moves passwordSet, a FAILED one usually means the row is
        // stale -- and the correct icon for it is the one this refresh picks.
        if (response.status) await getAllUsers();
      } catch (err) {
        setResult({
          error: true,
          message: (err.response && err.response.data && err.response.data.message)
            || err.message
            || action.failed(),
        });
      } finally {
        setBusy(false);
        setPending(null);
      }
    }

    useEffect(()=>{
        getAllUsers();
      },[setData])

    return (
        <div className="batchOperations">
            <div class="row mb-2">
                <div class="col-sm-6">
                    <h2 class="m-0 text-dark">{t('user:list.title')}</h2>
                </div>
            </div>
            {result ? (
              <div className={result.error ? 'alert alert-danger' : 'alert alert-success'} role="alert">
                {result.message}
              </div>
            ) : null}
            <div className="card">
                <div class="card-body">
                {/* Filter bar. No page in the app used DataTable's subHeader
                    before, so this sits above the table as a plain row of
                    form-controls -- same components and sizing as every other
                    filter strip in the app (see BatchHistory, DeviceManagement). */}
                <div className="row mb-2 usersFilterBar">
                    <div className="col-md-4 mb-2">
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder={t('user:list.searchPlaceholder')}
                            aria-label={t('common:actions.search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="col-md-2 mb-2">
                        <select className="form-control form-control-sm" value={roleFilter}
                                aria-label={t('common:fields.role')}
                                onChange={(e) => setRoleFilter(e.target.value)}>
                            <option value="">{t('user:list.allRoles')}</option>
                            <option value={Role.Admin}>{t('common:roles.admin')}</option>
                            <option value={Role.Farmer}>{t('common:roles.farmer')}</option>
                            <option value={Role.Worker}>{t('common:roles.worker')}</option>
                            <option value={Role.Digiviewer}>{t('common:roles.viewer')}</option>
                        </select>
                    </div>
                    <div className="col-md-2 mb-2">
                        <select className="form-control form-control-sm" value={statusFilter}
                                aria-label={t('common:fields.status')}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">{t('user:list.allStatuses')}</option>
                            <option value="ACTIVE">{t('common:state.active')}</option>
                            <option value="INACTIVE">{t('common:state.inactive')}</option>
                        </select>
                    </div>
                    <div className="col-md-2 mb-2">
                        {/* Only Workers and Viewers carry a farmowner, so this
                            narrows to one farmer's people -- and to the farmer
                            themselves, which is what "farmer wise" implies. */}
                        <Select
                            inputId="usersFarmerFilter"
                            aria-label={t('common:entity.farmer')}
                            options={farmerSelectOptions}
                            value={farmerSelectOptions.find((o) => o.value === farmerFilter) || null}
                            onChange={(opt) => setFarmerFilter(opt ? opt.value : '')}
                            placeholder={t('user:list.allFarmers')}
                            isClearable
                            isSearchable
                            styles={farmerSelectStyles}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            noOptionsMessage={() => t('common:state.noDataFound')}
                        />
                    </div>
                    <div className="col-md-2 mb-2 d-flex align-items-center justify-content-between">
                        {isAdmin ? (
                            <label className="usersFilterCheck mb-0">
                                <input type="checkbox" checked={pendingOnly}
                                       onChange={(e) => setPendingOnly(e.target.checked)} />
                                <span>{t('user:list.invitePendingOnly')}</span>
                            </label>
                        ) : <span />}
                        {filtersActive ? (
                            <button type="button" className="btn btn-sm btn-link p-0"
                                    onClick={clearFilters}>
                                {t('common:actions.reset')}
                            </button>
                        ) : null}
                    </div>
                </div>
                {filtersActive ? (
                    <p className="font12 notEntered mb-2">
                        {t('user:list.showingCount', { shown: filtered.length, total: data.length })}
                    </p>
                ) : null}
                <DataTable
                    columns={columns}
                    data={filtered}
                    defaultSortField="templateID"
                    pagination
                    dense
                    selectableRowsHighlight = 'true'
                    compact
                    highlightOnHover = 'true'
                    noDataComponent={
                        <div className="p-3 notEntered">
                            {filtersActive ? t('user:list.noMatch') : t('common:state.noDataFound')}
                        </div>
                    }
                />
                </div>
            </div>

            <Modal show={action !== null} onHide={() => setPending(null)} centered>
              <Modal.Header>
                <Modal.Title>{action ? action.title(pending) : ''}</Modal.Title>
              </Modal.Header>
              <Modal.Body>{action ? action.body(pending) : ''}</Modal.Body>
              <Modal.Footer>
                <button type="button" className="modalCancelBtn" disabled={busy}
                        onClick={() => setPending(null)}>
                  {t('common:actions.cancel')}
                </button>
                <button type="button"
                        className={action ? action.confirmClass(pending) : 'btn btn-primary'}
                        disabled={busy}
                        onClick={confirmPending}>
                  {busy ? t('common:state.loading') : (action ? action.confirmLabel(pending) : '')}
                </button>
              </Modal.Footer>
            </Modal>
        </div>

    );
}
export default Users;
